import React, { useMemo } from "react";
import { Link, useLocation } from "react-router-dom";

import logo from "../../assets/elitlig logo beyaz.png";
import FooterArchive from "./FooterArchive";
import { SiteSponsorStrip } from "../SiteSponsors";
import "./SiteChrome.css";
import { usePortal } from "../../portal/PortalContext";
import PortalFooter from "../../portal/PortalFooter";

const encodeSeg = (value) =>
  encodeURIComponent(String(value ?? "").trim());

const getOptionId = (option) => {
  if (option === null || option === undefined) return "";

  if (typeof option === "object") {
    return String(
      option.id ??
        option.value ??
        option.cityId ??
        option.leagueId ??
        option.seasonId ??
        ""
    ).trim();
  }

  return String(option).trim();
};

const getOptionLabel = (option) => {
  if (option === null || option === undefined) return "";

  if (typeof option === "object") {
    return (
      option.label ??
      option.name ??
      option.ad ??
      option.title ??
      String(option.id ?? option.value ?? "")
    );
  }

  return String(option);
};

/** Özel lig sayfasında (Junior Cup) o sayfanın kendi alt bilgisi çizilir. */
export default function SiteFooter(props) {
  const { portal } = usePortal();
  if (portal) return <PortalFooter portal={portal} />;
  return <ElitligFooter {...props} />;
}

function ElitligFooter({
  cityOptions = [],
  leagueOptions = [],
  seasonOptions = [],

  selectedCity = "",
  selectedLeague = "",
  selectedSeason = "",

  onCityChange,
  onLeagueChange,
  onSeasonChange,

  scopeLabel = "",
  showFilters = true,
}) {
  const location = useLocation();
  const year = new Date().getFullYear();

  const selectedCityId = getOptionId(selectedCity);
  const selectedLeagueId = getOptionId(selectedLeague);
  const selectedSeasonId = getOptionId(selectedSeason);

  const routeScope = useMemo(() => {
    const parts = location.pathname
      .split("/")
      .filter(Boolean);

    const cityIndex = parts.indexOf("sehir");
    const leagueIndex = parts.indexOf("lig");
    const seasonIndex = parts.indexOf("sezon");

    const cityId =
      cityIndex >= 0
        ? decodeURIComponent(parts[cityIndex + 1] || "")
        : "";

    const leagueId =
      leagueIndex >= 0
        ? decodeURIComponent(parts[leagueIndex + 1] || "")
        : "";

    const seasonId =
      seasonIndex >= 0
        ? decodeURIComponent(parts[seasonIndex + 1] || "")
        : "";

    return {
      cityId,
      leagueId,
      seasonId,
    };
  }, [location.pathname]);

  /*
   * Öncelik:
   * 1. Üst bileşenden gelen seçili değer
   * 2. URL içerisindeki değer
   */
  const cityId = selectedCityId || routeScope.cityId;
  const leagueId = selectedLeagueId || routeScope.leagueId;
  const seasonId = selectedSeasonId || routeScope.seasonId;

  const cityReady = Boolean(cityId);
  const leagueReady = Boolean(cityId && leagueId);
  const seasonReady = Boolean(cityId && leagueId && seasonId);

  const scope = useMemo(() => {
    if (seasonReady) {
      const base = [
        "/sehir",
        encodeSeg(cityId),
        "lig",
        encodeSeg(leagueId),
        "sezon",
        encodeSeg(seasonId),
      ].join("/");

      return {
        ready: true,
        cityId,
        leagueId,
        seasonId,
        home: `${base}/anasayfa`,

        league: [
          {
            to: `${base}/anasayfa`,
            label: "Anasayfa",
          },
          {
            to: `${base}/maclar`,
            label: "Fikstür & Sonuçlar",
          },
          {
            to: `${base}/puan-durumu`,
            label: "Puan Durumu",
          },
          {
            to: `${base}/oyuncular`,
            label: "Oyuncular",
          },
        ],

        info: [
          {
            to: `/sehir/${encodeSeg(cityId)}/kurallar`,
            label: "Lig Kuralları",
          },
          {
            to: `/sehir/${encodeSeg(cityId)}/cezalar`,
            label: "Ceza Yönetmeliği",
          },
          {
            to: `/sehir/${encodeSeg(cityId)}/iletisim`,
            label: "İletişim",
          },
        ],
      };
    }

    if (cityReady) {
      const cityBase = `/sehir/${encodeSeg(cityId)}`;

      return {
        ready: false,
        cityReady: true,
        cityId,
        leagueId: "",
        seasonId: "",
        home: "/",

        league: [
          {
            to: "/",
            label: "Lig ve Sezon Seç",
          },
          {
            to: `${cityBase}/kurallar`,
            label: "Lig Kuralları",
          },
          {
            to: `${cityBase}/cezalar`,
            label: "Cezalar",
          },
        ],

        info: [
          {
            to: `${cityBase}/kurallar`,
            label: "Lig Kuralları",
          },
          {
            to: `${cityBase}/cezalar`,
            label: "Ceza Yönetmeliği",
          },
          {
            to: `${cityBase}/iletisim`,
            label: "İletişim",
          },
        ],
      };
    }

    return {
      ready: false,
      cityReady: false,
      cityId: "",
      leagueId: "",
      seasonId: "",
      home: "/",

      league: [
        {
          to: "/",
          label: "Şehir Seç",
        },
        {
          to: "/haberler",
          label: "Haberler",
        },
      ],

      info: [
        {
          to: "/giris",
          label: "Giriş",
        },
        {
          to: "/kayit-ol",
          label: "Kayıt Ol",
        },
      ],
    };
  }, [
    cityId,
    leagueId,
    seasonId,
    cityReady,
    seasonReady,
  ]);

  const matchesLink = scope.ready
    ? scope.league.find((item) =>
        item.to.endsWith("/maclar")
      )?.to || scope.home
    : "/haberler";

  const rulesLink = cityReady
    ? `/sehir/${encodeSeg(cityId)}/kurallar`
    : "/";

  const contactLink = cityReady
    ? `/sehir/${encodeSeg(cityId)}/iletisim`
    : "/";

  const handleCityChange = (event) => {
    onCityChange?.(event.target.value);
  };

  const handleLeagueChange = (event) => {
    onLeagueChange?.(event.target.value);
  };

  const handleSeasonChange = (event) => {
    onSeasonChange?.(event.target.value);
  };

  return (
    <footer className="el-site-footer">
      <div
        className="el-footer-pitch"
        aria-hidden="true"
      />

      <SiteSponsorStrip />

      <div className="el-footer-cta-band">
        <div className="el-footer-cta-band__inner">
          <div className="el-footer-cta-band__text">
            <h2>Sahaya çıkma vakti</h2>

            <p>
              Şehrini seç, ligini takip et — maçlar,
              tablolar ve yıldız oyuncular her zaman güncel.
            </p>
          </div>

          <div className="el-footer-cta-band__actions">
            <Link to={scope.ready ? scope.league[1].to : "/"} className="el-footer-btn el-footer-btn--gold">
              {scope.ready ? "Maçlara Git" : "Ligleri Keşfet"}
            </Link>
            <Link to={scope.ready ? scope.league[2].to : "/haberler"} className="el-footer-btn el-footer-btn--ghost">
              {scope.ready ? "Puan Durumu" : "Haberler"}
            </Link>
          </div>
        </div>
      </div>

      <div className="el-footer-main">
        <div className="el-footer-brandcol">
          <Link
            to={scope.home}
            className="el-footer-brandcol__logo"
          >
            <img
              src={logo}
              alt="ElitLig"
            />

            <strong>ELİTLİG</strong>
          </Link>

          <p>
            Türkiye&apos;nin profesyonel halı saha ligi.
            Canlı skorlar, puan durumu, oyuncu istatistikleri
            ve maç içerikleri tek çatı altında.
          </p>

          <div className="el-footer-badges">
            <span className="el-footer-badge">
              <em>●</em>
              Canlı Skor
            </span>

            <span className="el-footer-badge">
              <em>●</em>
              Şehir Ligleri
            </span>

            <span className="el-footer-badge">
              <em>●</em>
              Oyuncu Profilleri
            </span>
          </div>
        </div>

        <div className="el-footer-col">
          <h3>Lig</h3>

          <ul>
            {scope.league.map((item) => (
              <li key={`${item.to}-${item.label}`}>
                <Link to={item.to}>
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="el-footer-col">
          <h3>Bilgi</h3>

          <ul>
            {scope.info.map((item) => (
              <li key={`${item.to}-${item.label}`}>
                <Link to={item.to}>
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="el-footer-col">
          <h3>Platform</h3>

          <ul>
            <li>
              <Link to="/">
                Şehir Haritası
              </Link>
            </li>

            <li>
              <Link to="/oyuncu-listesi">
                Oyuncu Listesi
              </Link>
            </li>

            <li>
              <Link to="/icerik-havuzu">
                İçerik Stüdyosu
              </Link>
            </li>

            <li>
              <Link to="/arsiv">
                Arşiv
              </Link>
            </li>

            <li>
              <Link to="/giris">
                Yönetim Girişi
              </Link>
            </li>
          </ul>
        </div>
      </div>

      <FooterArchive />

      <div className="el-footer-bottom">
        <div className="el-footer-bottom__inner">
          <span>
            © {year} ElitLig. Tüm hakları saklıdır.
          </span>

          <div className="el-footer-bottom__links">
            <Link to={rulesLink}>
              Kurallar
            </Link>

            <Link to={contactLink}>
              İletişim
            </Link>

            {/* Doğrudan verilebilen hesap silme adresi: Google Play'in Data
                safety formu ve KVKK başvuruları bu bağlantıyı ister. */}
            <Link to="/hesap-sil">
              Hesap Silme
            </Link>

            {/* Mağaza kayıtları (App Store / Google Play) ve KVKK aydınlatması
                için herkese açık gizlilik politikası. */}
            <Link to="/gizlilik-politikasi">
              Gizlilik Politikası
            </Link>
          </div>

          <span className="el-footer-bottom__tag">
            Premium Halı Saha Ligi
          </span>
        </div>
      </div>

    </footer>
  );
}
